import { FLIP_CONTRACT_ADDRESSES, STATE_CHAIN_GATEWAY_ADDRESSES } from '@chainflip/utils/addresses';
import * as ss58 from '@chainflip/utils/ss58';
import { isHex } from '@chainflip/utils/string';
import { useMutation, useQuery } from '@tanstack/react-query';
import { secondsToMilliseconds } from 'date-fns';
import { erc20Abi } from 'viem';
import { useAccount, usePublicClient, useWalletClient } from 'wagmi';
import stateChainGateway from '@/shared/abi/stateChainGateway';
import useRpcRequest from '@/shared/hooks/useRpcRequest';
import { getKnownChainflipNetwork } from '@/shared/utils/env';
import TokenAmount from '@/shared/utils/TokenAmount';

type MutationHook<Args = unknown, Result = unknown> = {
  onSuccess?: (result: Result) => void;
  onError?: (error: Error) => void;
  onMutate?: (args: Args) => void;
};

export const useFundAccount = ({
  onSuccess,
  onError,
  onMutate,
}: MutationHook<
  { accountId: `0x${string}` | `cF${string}`; amount: bigint },
  { hash: `0x${string}`; amount: bigint }
> = {}) => {
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();

  const mutation = useMutation({
    mutationFn: async ({
      accountId,
      amount,
    }: {
      accountId: `0x${string}` | `cF${string}`;
      amount: bigint;
    }) => {
      const { request } = await publicClient!.simulateContract({
        abi: stateChainGateway,
        functionName: 'fundStateChainAccount',
        args: [isHex(accountId) ? accountId : ss58.toPublicKey(accountId), amount],
        address: STATE_CHAIN_GATEWAY_ADDRESSES[getKnownChainflipNetwork()],
        chain: walletClient!.chain,
        account: walletClient!.account,
      });

      const hash = await walletClient!.writeContract(request);

      await publicClient!.waitForTransactionReceipt({ hash });

      return { hash, amount };
    },
    onMutate,
    onError,
    onSuccess,
  });

  return mutation;
};

export const useExecuteRedemption = ({ onSuccess, onError }: MutationHook = {}) => {
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();

  const mutation = useMutation({
    mutationFn: async (accountId: `0x${string}` | `cF${string}`) => {
      const { request } = await publicClient!.simulateContract({
        abi: stateChainGateway,
        functionName: 'executeRedemption',
        args: [isHex(accountId) ? accountId : ss58.toPublicKey(accountId)],
        address: STATE_CHAIN_GATEWAY_ADDRESSES[getKnownChainflipNetwork()],
        chain: walletClient!.chain,
        account: walletClient!.account,
      });

      const hash = await walletClient!.writeContract(request);

      await publicClient!.waitForTransactionReceipt({ hash });

      return hash;
    },
    onError,
    onSuccess,
  });

  return mutation;
};

export type PendingRedemption = NonNullable<ReturnType<typeof usePendingRedemption>['data']>;

export const usePendingRedemption = (
  accountId: `0x${string}` | `cF${string}` | undefined,
  { refetchInterval }: { refetchInterval?: number } = {},
) => {
  const publicClient = usePublicClient();

  const functionName = 'getPendingRedemption' as const;

  const query = useQuery({
    queryKey: [functionName, accountId],
    queryFn: async () => {
      const redemption = await publicClient!.readContract({
        abi: stateChainGateway,
        functionName,
        args: [isHex(accountId!) ? accountId : ss58.toPublicKey(accountId!)],
        address: STATE_CHAIN_GATEWAY_ADDRESSES[getKnownChainflipNetwork()],
      });

      if (redemption.startTime === 0) return null;
      return redemption;
    },
    enabled: Boolean(publicClient && accountId),
    refetchInterval,
    select: (data) =>
      data && {
        ...data,
        startTime: secondsToMilliseconds(data.startTime),
        expiryTime: secondsToMilliseconds(data.expiryTime),
      },
  });

  return query;
};

export const useApproveStateChainGateway = ({
  onSuccess,
  onError,
  onMutate,
}: MutationHook<bigint, { hash: `0x${string}`; amount: bigint } | null> = {}) => {
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();

  const mutation = useMutation({
    mutationFn: async (requiredAmount: bigint) => {
      const scGatewayAddress = STATE_CHAIN_GATEWAY_ADDRESSES[getKnownChainflipNetwork()];
      const flipAddress = FLIP_CONTRACT_ADDRESSES[getKnownChainflipNetwork()];

      const currentAllowance = await publicClient!.readContract({
        address: flipAddress,
        abi: erc20Abi,
        functionName: 'allowance',
        args: [walletClient!.account.address, scGatewayAddress],
      });

      if (currentAllowance >= requiredAmount) return null;

      const { request } = await publicClient!.simulateContract({
        abi: erc20Abi,
        address: flipAddress,
        functionName: 'approve',
        account: walletClient!.account,
        args: [scGatewayAddress, requiredAmount],
        chain: walletClient!.chain,
      });

      const hash = await walletClient!.writeContract(request);

      await publicClient!.waitForTransactionReceipt({ hash });

      return { hash, amount: requiredAmount };
    },
    onMutate,
    onError,
    onSuccess,
  });

  return mutation;
};

export const useMinimumFunding = () => {
  const publicClient = usePublicClient();

  const functionName = 'getMinimumFunding' as const;

  const query = useQuery({
    queryKey: [functionName],
    queryFn: () =>
      publicClient!.readContract({
        abi: stateChainGateway,
        functionName,
        address: STATE_CHAIN_GATEWAY_ADDRESSES[getKnownChainflipNetwork()],
      }),
    enabled: Boolean(publicClient),
  });

  return query;
};

export const useRedemptionDelay = () => {
  const publicClient = usePublicClient();

  const functionName = 'REDEMPTION_DELAY' as const;

  const query = useQuery({
    queryKey: [functionName],
    queryFn: () =>
      publicClient!.readContract({
        abi: stateChainGateway,
        functionName,
        address: STATE_CHAIN_GATEWAY_ADDRESSES[getKnownChainflipNetwork()],
      }),
    enabled: Boolean(publicClient),
    select: secondsToMilliseconds,
  });

  return query;
};

export const useRedemptionTax = () =>
  useRpcRequest({
    method: 'cf_funding_environment',
    args: [],
    select: (data) => data.redemption_tax,
  });

export const useFlipBalance = (otherAddress?: `0x${string}`) => {
  const publicClient = usePublicClient();

  const functionName = 'balanceOf' as const;

  const { address: connectedAddress } = useAccount();

  const address = otherAddress ?? connectedAddress;

  const query = useQuery({
    queryKey: [functionName],
    queryFn: () =>
      publicClient!.readContract({
        abi: erc20Abi,
        functionName,
        address: FLIP_CONTRACT_ADDRESSES[getKnownChainflipNetwork()],
        args: [address!],
      }),
    enabled: Boolean(publicClient && address),
    select: (data) => TokenAmount.fromAsset(data, 'Flip'),
  });

  return query;
};
